import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'; // 'useMemo' is now imported
// import './style/style.css'
// Import the new Spinner component
import Spinner from './Spinner'; 
import './Spinner.css'; // Import the spinner styles

import Header from './Header';
import Footer from './Footer';
import Legend from './Legend';
import Offerings from './Offerings';
import Festivals from './Festivals';
import Theyyam from './Theyyam';
import Committee from './Committee';
import Gallery from './Gallery';
import Modal from './Modal';
import ImageViewer from './ImageViewer';
import { modalContents } from '../data/modalData';
 

// Asset Imports
import templeImage from '../assets/kavumainpageimg/bg-kavu.png';
import lampImage from '../assets/kavumainpageimg/kedavilakk.png';
// import theyyamPageBg from '../assets/kavumainpageimg/theyyam.webp';
import poomalaKavu from '../assets/kavumainpageimg/poomalaKavu.webp';


/**
 * Main component for the temple website.
 * Manages state for scroll effects, modals, and navigation.
 */
const KavuMainPage = () => {

     const [isLoading, setIsLoading] = useState(true);
    // State management for UI interactions
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [modalInfo, setModalInfo] = useState({ isOpen: false, type: '', data: null });
    const [imageViewerSrc, setImageViewerSrc] = useState(null);
    const [showFixedBg, setShowFixedBg] = useState(true);

    // State for dynamic background and lamp animations
    const [visualProgress, setVisualProgress] = useState(0);
    const [lampOpacity, setLampOpacity] = useState(0);
    const [isLampVisible, setLampVisible] = useState(false);

    // Refs for DOM elements to calculate positions
    const page1Ref = useRef(null);
    const page3Ref = useRef(null);


       // ---- NEW: Add useEffect to handle the loading state ----
    useEffect(() => {
        // Simulate loading time to ensure spinner is visible for a short period
        // This helps prevent a jarring flash on fast connections
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 1500); // 1.5 seconds, adjust as needed

        // Cleanup the timer if the component unmounts
        return () => clearTimeout(timer);
    }, []); // Empty dependency array ensures this runs only once on mount



    // Throttled scroll handler for performance
    const handleScroll = useCallback(() => {
        const scrollPosition = window.scrollY;
        const windowHeight = window.innerHeight;

        // Navbar scroll effect
        setIsScrolled(scrollPosition > 50);

        // Logic to hide the fixed background when scrolling past a certain point
        if (page3Ref.current) {
            const page3Top = page3Ref.current.offsetTop;
            const hideThreshold = page3Top - windowHeight * 0.5;
            setShowFixedBg(scrollPosition < hideThreshold);
        }
        
        // Day/Night sky transition effect
        const nightModeThreshold = windowHeight * 0.3;
        const progress = Math.min(1, scrollPosition / nightModeThreshold);
        setVisualProgress(progress);

        // Lamp visibility and animation logic
        if (page1Ref.current) {
            const page1Top = page1Ref.current.offsetTop;
            const page1Height = page1Ref.current.offsetHeight;

            if (scrollPosition + windowHeight > page1Top && scrollPosition < page1Top + page1Height) {
                setLampVisible(true);
                const progressInPage1 = (scrollPosition + windowHeight - page1Top) / (windowHeight + page1Height);
                setLampOpacity(0.4 * Math.sin(progressInPage1 * Math.PI));
            } else {
                setLampVisible(false);
                setLampOpacity(0);
            }
        }
    }, []);

    useEffect(() => {
        // Don't add scroll listener until the page has loaded
        if (!isLoading) {
            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => window.removeEventListener('scroll', handleScroll);
        }
    }, [isLoading, handleScroll]); // Re-run when isLoading changes

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // Handlers for UI elements
    const toggleMobileMenu = () => setMobileMenuOpen(prev => !prev);
    const openModal = (type, data) => setModalInfo({ isOpen: true, type, data });
    const closeModal = () => setModalInfo({ isOpen: false, type: '', data: null });
    const openImageViewer = (src) => setImageViewerSrc(src);
    const closeImageViewer = () => setImageViewerSrc(null);

    // Effect to toggle 'no-scroll' class on body
    useEffect(() => {
        const hasOpenOverlay = modalInfo.isOpen || !!imageViewerSrc || isMobileMenuOpen;
        document.body.classList.toggle('no-scroll', hasOpenOverlay);
    }, [modalInfo.isOpen, imageViewerSrc, isMobileMenuOpen]);

    // Star generation for the night sky, memoized for performance
    const stars = useMemo(() => Array.from({ length: 200 }, (_, i) => {
        const size = Math.random();
        const style = {
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${2 + Math.random() * 3}s`,
        };
        let className = 'star ';
        if (size < 0.6) className += 'star-small';
        else if (size < 0.9) className += 'star-medium';
        else className += 'star-large';
        return <div key={i} className={className} style={style}></div>;
    }), []); // Empty dependency array means this runs only once


     if (isLoading) {
        return <Spinner />;
    }


    
    return (
        <>
            <Header isScrolled={isScrolled} isMobileMenuOpen={isMobileMenuOpen} toggleMobileMenu={toggleMobileMenu} />

            <div className={`sky-container ${!showFixedBg ? 'hide-fixed-bg' : ''}`} style={{ opacity: showFixedBg ? 1 - visualProgress / 2 : 0 }}>
                <div className="sky" style={{ background: `linear-gradient(to bottom, rgb(${30 + (-28 * visualProgress)}, ${144 + (-143 * visualProgress)}, ${255 + (-238 * visualProgress)}), rgb(${135 + (-125 * visualProgress)}, ${206 + (-196 * visualProgress)}, ${235 + (-193 * visualProgress)}), rgb(${224 + (-179 * visualProgress)}, ${247 + (-202 * visualProgress)}, ${250 + (-160 * visualProgress)}))` }}></div>
                <div className="clouds-back" style={{ opacity: 0.5 - 0.4 * visualProgress }}></div>
                <div className="clouds-mid" style={{ opacity: 0.4 - 0.35 * visualProgress }}></div>
                <div className="clouds-front" style={{ opacity: 0.4 - 0.4 * visualProgress }}></div>
                <div className="stars" style={{ opacity: visualProgress }}>{stars}</div>
                <div className="moon" style={{ opacity: visualProgress }}></div>
            </div>

            <div className={`temple-container ${!showFixedBg ? 'hide-fixed-bg' : ''}`} style={{ opacity: showFixedBg ? 1 : 0 }}>
                <img src={templeImage} alt="" className="temple" style={{ filter: `brightness(${1 - 0.5 * visualProgress}) hue-rotate(${10 * visualProgress}deg)`}}/>
            </div>
            
            <div className={`lamp-container ${isLampVisible ? 'visible' : ''}`} style={{ opacity: lampOpacity }}>
                <img src={lampImage} alt="Kedavilakku Lamp" className="lamp" />
            </div>

            <main className="content-section" style={{ opacity: isScrolled ? 1 : 0.1 }}>
                <div className="page" id="page1" ref={page1Ref}>
                    <h2 className="content-title">കെടാവിളക്ക്</h2>
                    <div className="content-text">
                        <p>  ശ്രീ തലയന്നേരി പൂമാല ഭഗവതി കാവിലെ ആചാരാനുഷ്ഠാനങ്ങളിൽ ഏറ്റവും ശ്രേഷ്ഠമായത് ഗർഭഗൃഹത്തിൽ പ്രകാശിച്ചു കൊണ്ടിരിക്കുന്ന കെടാ ദീപമാണ് ഒരിക്കലും അണിഞ്ഞു പോകാതെ ഭക്തിയോടെ ഈ ദിവ്യ തേജസിനെ ഭക്തിയോടെപരിപാലിച്ചു പോരുന്നു, ദേവി സാന്നിധ്യ പ്രതീകമായ ഈ ദീപം സദാ പ്രകാശിച്ചു കൊണ്ടേയിരിക്കും മറ്റൊരു കാവിലും ഈ രീതിയിൽ കെടാവിളക്ക് പരിപാലിച്ചു പോരുന്നില്ല, ഉത്സവ കാലത്ത്ചെക്കിപ്പൂവ് തുളസി എന്നിവ കൊണ്ട് ദീപ പൂജ ചെയ്തു ദേവി ചൈതന്യം വർധിപ്പിക്കുന്നു. ദീപ ലക്ഷണം നോക്കി ശുഭാ അശുഭ വിചിന്തനം നടത്തുന്ന പതിവും ഇവിടെയുണ്ട്. തലയന്നേരി കാവിലെ മുഖ്യ പ്രാർത്ഥനയും കെടാവിളക്കിലെ എണ്ണ സമർപ്പണം തന്നെ, വിളക്കിൽ നിന്നും എടുത്തു മാറ്റുന്ന പഴയ എണ്ണ ദേഹ ദുരിതങ്ങൾ ഏൽക്കുന്നവർക്ക് ദിവ്യ ഔഷധമായി ഉപയോഗിക്കാറുണ്ട്.</p>
                    </div>
                </div>

                <div className="page" id="page2">
                    <div className="page2-content-wrapper">
                         <div className="page2-image-container">
                            <img src={poomalaKavu} alt="Theyyam Performance" className="page2-image" />
                        </div>
                        <div className="page2-text-container">
                            <h2 className="content-title">പൂമാല ഭഗവതി കാവ്</h2>
                            <div className="content-text">
                                <p>പയ്യന്നൂരിന്റെ സാംസ്കാരികതയിൽ നിറഞ്ഞു നിൽക്കുന്ന അന്നൂരിന്റെ ഭൂമികയിൽ ദേവീചൈയ്തന്യം പ്രസരിപ്പിക്കുന്ന അതിപുരാതനമായ ദേവിസങ്കേതമാണ് ആര്യ പൂങ്കന്നിയുടെ ആരൂഢസ്ഥാനമായ അന്നൂർ ശ്രീ തലയന്നേരി  പൂമാല ഭഗവതി കാവ് .
പ്രകൃതിഭംഗിയോടെ ആനുഗ്രഹീതമായ ഈ കാവിന്റെ സമീപത്തായി വിശ്വപരിപാലകനായ അന്നദാദാവ്  വിഷ്ണുഭഗവാന്റെ വീശിഷ്ട ദേവസ്ഥാനം സ്ഥിതിചെയ്യുന്നു.നാട്ടുകൂട്ടത്തിന്റെ നിത്യപൂജകളേറ്റുവാങ്ങികൊണ്ട് ഐശ്വര്യദേവതയായ പൂമാല അനുഗ്രഹം ചൊരിഞ്ഞു കൊണ്ട് കാവിൽ വിളങ്ങുന്നു.മീനമാസത്തിലെ പൂരം നാളിൽ വിഷ്ണു ക്ഷേത്രത്തിന്റെ കിഴക്കേ ഗോപുരത്തറയിൽ എഴുന്നള്ളുന്ന പൂമാല,പയ്യന്നുർ അധിപനായ സുബ്രഹ്മണ്യന്റെ നിഴലിൽ  നിന്നുകൊണ്ട് മഹാവിഷ്ണുവിന്റെ സാന്നിധ്യത്തിൽ ഭക്തജനകളുടെ സങ്കടം കേട്ട് സമാധാനമരുളുന്ന അപൂർവമായ ദേവസമാഗമം ഈ ദേശത്തെ ധന്യമാക്കുന്നു.</p>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div ref={page3Ref}>
                    <Legend />
                </div>

                <Offerings openModal={openModal} modalContents={modalContents} />
                <Festivals openImageViewer={openImageViewer} />
                <Theyyam openImageViewer={openImageViewer} />
                <Committee openModal={openModal} />
                <Gallery />
            </main>

            <Footer />
 <button id="scrollToTopBtn" title="Go to top">▲</button>
            {modalInfo.isOpen && <Modal type={modalInfo.type} data={modalInfo.data} onClose={closeModal} />}
            {imageViewerSrc && <ImageViewer src={imageViewerSrc} onClose={closeImageViewer} />}
        </>
    );
};

export default KavuMainPage;